import React, { useEffect, useRef, useState } from 'react';

export type ChatEntry = { prompt: string; response: string };

interface AthenaConsoleProps {
  chat: ChatEntry[];
  setChat: React.Dispatch<React.SetStateAction<ChatEntry[]>>;
}

function extractJSONObjects(buffer: string): { objects: any[]; rest: string } {
  const objects: any[] = [];
  let depth = 0;
  let start = -1;

  for (let i = 0; i < buffer.length; i++) {
    const char = buffer[i];

    if (char === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (char === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        const jsonStr = buffer.slice(start, i + 1);
        try {
          objects.push(JSON.parse(jsonStr));
        } catch {
          // ignore malformed JSON
        }
        start = -1;
      }
    }
  }

  return {
    objects,
    rest: depth === 0 ? '' : buffer.slice(start),
  };
}

const AthenaConsole: React.FC<AthenaConsoleProps> = ({ chat, setChat }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [chat, loading]);

  const submitPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    const userPrompt = prompt;
    setPrompt('');
    setLoading(true);
    setError(null);

    setChat((prev) => [...prev, { prompt: userPrompt, response: '' }]);

    try {
      const res = await fetch('https://athena-616.ngrok.io/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: userPrompt }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Bad response: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        if (buffer.includes('[DONE]')) {
          // Final flush: try to extract any remaining JSON
          const { objects } = extractJSONObjects(buffer);

          for (const obj of objects) {
            const token =
              obj?.content ??
              obj?.data?.choices?.[0]?.delta?.content ??
              '';

            if (!token) continue;

            setChat((prev) => {
              const updated = [...prev];
              const last = updated.length - 1;
              updated[last] = {
                ...updated[last],
                response: updated[last].response + token,
              };
              return updated;
            });
          }

          break;
        }

        const { objects, rest } = extractJSONObjects(buffer);
        buffer = rest;

        for (const obj of objects) {
          const token =
            obj?.content ??
            obj?.data?.choices?.[0]?.delta?.content ??
            '';

          if (!token) continue;

          setChat((prev) => {
            const updated = [...prev];
            const last = updated.length - 1;
            updated[last] = {
              ...updated[last],
              response: updated[last].response + token,
            };
            return updated;
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Streaming error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div
        ref={feedRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: '#0c0f14',
          border: '1px solid #2a2f39',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '10px',
          color: '#cbd5e1',
          fontFamily: 'ui-monospace, SFMono-Regular, Consolas, Menlo, monospace',
        }}
      >
        {chat.length === 0 && (
          <p style={{ color: '#77808f', textAlign: 'center', margin: '24px 0' }}>
            No conversation yet. Ask Athena anything...
          </p>
        )}

        {chat.map((entry, idx) => (
          <div key={idx} style={{ marginBottom: '1.25rem' }}>
            <div style={{ color: '#58a6ff' }}>
              <strong>You:</strong> {entry.prompt}
            </div>
            <div style={{ color: '#80ffb0', whiteSpace: 'pre-wrap' }}>
              <strong>Athena:</strong> {entry.response}
            </div>
          </div>
        ))}
      </div>

      <form
        onSubmit={submitPrompt}
        style={{
          display: 'flex',
          gap: '8px',
          padding: '10px',
          borderTop: '1px solid #333',
          backgroundColor: '#111',
          marginLeft: '84px',
        }}
      >
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Ask Athena anything..."
          style={{
            flex: 1,
            padding: '12px 14px',
            backgroundColor: '#0d1117',
            color: '#e6edf3',
            border: '1px solid #2b303b',
            borderRadius: '20px',
          }}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '42px',
            height: '42px',
            borderRadius: '50%',
            backgroundColor: '#3a86ff',
            color: '#fff',
            border: 'none',
          }}
        >
          {loading ? '…' : '➤'}
        </button>
      </form>

      {error && <p style={{ color: '#ff6b6b' }}>❌ {error}</p>}
    </div>
  );
};

export default AthenaConsole;
